import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navigation from "@/components/Navigation";
import TopHeader from "@/components/TopHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { 
  Heart, X, MapPin, Sparkles, Baby, 
  RefreshCw, Users, ChevronLeft, ChevronRight, MessageCircle, User, Eye, Briefcase
} from "lucide-react";

const Discover = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  const [matches, setMatches] = useState([]);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [newMatch, setNewMatch] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [discoverProfileModal, setDiscoverProfileModal] = useState(false);
  const [selectedDiscoverUser, setSelectedDiscoverUser] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useEffect(() => {
    fetchUsers();
    fetchMatches();
    fetchPendingRequests();
  }, [filter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = filter ? `?phase=${filter}` : '';
      const response = await axios.get(`${API}/users/discover${params}`, { withCredentials: true });
      setUsers(response.data);
      setCurrentIndex(0);
    } catch (error) {
      console.error("Fetch users error:", error);
      toast.error("Failed to load profiles");
    } finally {
      setLoading(false);
    }
  };

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/connections/matches`, { withCredentials: true });
      setMatches(response.data);
    } catch (error) {
      console.error("Fetch matches error:", error);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const response = await axios.get(`${API}/connections/requests`, { withCredentials: true });
      setPendingRequests(response.data);
    } catch (error) {
      console.error("Fetch pending requests error:", error);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await axios.post(`${API}/connections/accept/${requestId}`, {}, { withCredentials: true });
      toast.success("Connection accepted! 🎉");
      setProfileModalOpen(false);
      setSelectedRequest(null);
      fetchPendingRequests();
      fetchMatches();
    } catch (error) {
      toast.error("Failed to accept request");
    }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      await axios.post(`${API}/connections/decline/${requestId}`, {}, { withCredentials: true });
      toast.info("Request declined");
      setProfileModalOpen(false);
      setSelectedRequest(null);
      fetchPendingRequests();
    } catch (error) {
      toast.error("Failed to decline request");
    }
  };

  const viewRequestProfile = (request) => {
    setSelectedRequest(request);
    setProfileModalOpen(true);
  };

  const handleConnect = async (userId) => {
    try {
      const response = await axios.post(`${API}/connections/request/${userId}`, {}, { withCredentials: true });
      
      // Check if it's a match (mutual like)
      if (response.data.is_match) {
        const matchedUser = users[currentIndex];
        setNewMatch({
          user: matchedUser,
          connectionId: response.data.connection_id
        });
        setShowMatchModal(true);
        fetchMatches();
        toast.success("It's a match! 🎉");
      } else {
        toast.success("Connection request sent!");
      }
      setCurrentIndex(prev => prev + 1);
    } catch (error) {
      if (error.response?.data?.detail === "Connection request already exists") {
        toast.info("Already connected or pending");
      } else {
        toast.error("Failed to send request");
      }
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handleSkip = () => {
    setCurrentIndex(prev => prev + 1);
  };

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'ttc': return <Sparkles className="w-4 h-4" />;
      case 'pregnant': return <Heart className="w-4 h-4" />;
      case 'postpartum': return <Baby className="w-4 h-4" />;
      default: return <Heart className="w-4 h-4" />;
    }
  };

  const getPhaseLabel = (phase) => {
    switch (phase) {
      case 'ttc': return 'Trying to Conceive';
      case 'pregnant': return 'Pregnant';
      case 'postpartum': return 'Postpartum';
      default: return phase;
    }
  };

  const getChildrenLabel = (info) => {
    const labels = {
      'first-time-expecting': 'First-Time Expecting',
      'first-time-mom': 'First-Time Mom',
      'one-child': 'Mom of One',
      'two-children': 'Mom of Two',
      'three-plus': 'Mom of 3+',
      'twins': 'Twin Mom',
      'triplets-plus': 'Triplets or More'
    };
    return labels[info] || info;
  };

  const getWorkingLabel = (status) => {
    const labels = {
      'working-full-time': 'Working Full-Time',
      'working-part-time': 'Part-Time',
      'stay-at-home': 'Stay-at-Home Mom',
      'maternity-leave': 'Maternity Leave',
      'freelance': 'Freelance',
      'looking-for-work': 'Looking for Work',
      'student': 'Student'
    };
    return labels[status] || status;
  };

  const currentUser = users[currentIndex];

  return (
    <div className="min-h-screen bg-cream pb-24">
      <Navigation />
      <TopHeader title="Connect" />

      {/* Pending Connection Requests */}
      {pendingRequests.length > 0 && (
        <div className="px-6 mt-4 mb-6">
          <div className="max-w-2xl mx-auto">
            <Card className="bg-gradient-to-br from-clay/10 to-sage/10 border-clay/20">
              <CardContent className="p-4">
                <h3 className="font-medium text-sage-darker mb-3 flex items-center gap-2">
                  <Heart className="w-4 h-4 text-clay" />
                  Connection Requests ({pendingRequests.length})
                </h3>
                <div className="space-y-3">
                  {pendingRequests.map((request) => (
                    <div key={request.request_id} className="flex items-center gap-3 bg-white rounded-xl p-3">
                      <Avatar className="h-12 w-12 border-2 border-sage/30">
                        <AvatarImage src={request.from_user?.picture} />
                        <AvatarFallback className="bg-sage/20 text-sage-dark">
                          {request.from_user?.name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sage-darker font-medium truncate">{request.from_user?.name}</p>
                        <p className="text-muted-foreground text-sm truncate">
                          {request.from_user?.phase && getPhaseLabel(request.from_user.phase)}
                          {request.from_user?.location && ` • ${request.from_user.location}`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewRequestProfile(request)}
                          className="border-sage/30 text-sage-dark rounded-full"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleAcceptRequest(request.request_id)}
                          className="bg-sage text-white hover:bg-sage-dark rounded-full"
                        >
                          <Heart className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeclineRequest(request.request_id)}
                          className="border-sage/30 text-sage-dark rounded-full"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="px-6 mb-6">
        <div className="max-w-2xl mx-auto flex gap-2 overflow-x-auto pb-2">
          <Button 
            variant={filter === null ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(null)}
            className={`rounded-full ${filter === null ? 'bg-sage text-white' : 'border-sage/30 text-sage-darker'}`}
            data-testid="filter-all"
          >
            All
          </Button>
          <Button 
            variant={filter === 'ttc' ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter('ttc')}
            className={`rounded-full ${filter === 'ttc' ? 'bg-clay text-white' : 'border-sage/30 text-sage-darker'}`}
            data-testid="filter-ttc"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            TTC
          </Button>
          <Button 
            variant={filter === 'pregnant' ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter('pregnant')}
            className={`rounded-full ${filter === 'pregnant' ? 'bg-sage text-white' : 'border-sage/30 text-sage-darker'}`}
            data-testid="filter-pregnant"
          >
            <Heart className="w-4 h-4 mr-1" />
            Pregnant
          </Button>
          <Button 
            variant={filter === 'postpartum' ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter('postpartum')}
            className={`rounded-full ${filter === 'postpartum' ? 'bg-purple-500 text-white' : 'border-sage/30 text-sage-darker'}`}
            data-testid="filter-postpartum"
          >
            <Baby className="w-4 h-4 mr-1" />
            Postpartum
          </Button>
        </div>
      </div>

      {/* Swipe Cards */}
      <main className="px-6" data-testid="discover-main">
        <div className="max-w-2xl mx-auto">
          {loading ? (
            <div className="aspect-[3/4] max-h-[500px] bg-white rounded-3xl animate-pulse flex items-center justify-center border border-sage/20">
              <RefreshCw className="w-8 h-8 text-sage animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="aspect-[3/4] max-h-[500px] bg-white rounded-3xl flex flex-col items-center justify-center p-8 text-center border border-sage/20">
              <Users className="w-16 h-16 text-sage/40 mb-4" />
              <h3 className="text-xl font-serif text-sage-darker mb-2">No more profiles</h3>
              <p className="text-muted-foreground mb-6">
                {filter ? `No ${getPhaseLabel(filter)} profiles available right now` : "Check back later for new connections"}
              </p>
              <Button 
                onClick={fetchUsers}
                className="bg-sage text-white hover:bg-sage-dark rounded-full"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          ) : currentIndex >= users.length ? (
            <div className="aspect-[3/4] max-h-[500px] bg-white rounded-3xl flex flex-col items-center justify-center p-8 text-center border border-sage/20">
              <Heart className="w-16 h-16 text-sage mb-4" />
              <h3 className="text-xl font-serif text-sage-darker mb-2">You&apos;ve seen everyone!</h3>
              <p className="text-muted-foreground mb-6">Check back later for new profiles</p>
              <Button 
                onClick={fetchUsers}
                className="bg-sage text-white hover:bg-sage-dark rounded-full"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Start Over
              </Button>
            </div>
          ) : (
            <SwipeCard 
              user={currentUser} 
              onConnect={() => handleConnect(currentUser.user_id)}
              onSkip={handleSkip}
              onViewProfile={() => {
                setSelectedDiscoverUser(currentUser);
                setDiscoverProfileModal(true);
              }}
              getPhaseIcon={getPhaseIcon}
              getPhaseLabel={getPhaseLabel}
            />
          )}

          {/* Progress indicator */}
          {!loading && users.length > 0 && currentIndex < users.length && (
            <div className="mt-6 text-center">
              <p className="text-muted-foreground text-sm">
                {currentIndex + 1} of {users.length} profiles
              </p>
            </div>
          )}
        </div>

        {/* Your Connections Section */}
        {matches.length > 0 && (
          <div className="max-w-2xl mx-auto mt-12">
            <h2 className="text-xl font-serif text-sage-darker mb-4">Your Connections ({matches.length})</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {matches.map((match) => (
                <Card 
                  key={match.request_id} 
                  className="bg-white border-sage/20 hover:shadow-lg transition-shadow"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-14 h-14 border-2 border-sage/20">
                        <AvatarImage src={match.connected_user?.picture} />
                        <AvatarFallback className="bg-sage/20 text-sage-dark text-lg">
                          {match.connected_user?.name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sage-darker truncate">
                          {match.connected_user?.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {match.connected_user?.phase && getPhaseLabel(match.connected_user.phase)}
                          {match.connected_user?.location && ` • ${match.connected_user.location}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSelectedRequest({ from_user: match.connected_user, request_id: match.request_id });
                          setProfileModalOpen(true);
                        }}
                        className="flex-1 border-sage/30 text-sage-dark rounded-full"
                      >
                        <User className="w-4 h-4 mr-1" />
                        Profile
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => navigate(`/chat/${match.request_id}`)}
                        className="flex-1 bg-sage text-white hover:bg-sage-dark rounded-full"
                      >
                        <MessageCircle className="w-4 h-4 mr-1" />
                        Chat
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Match Modal */}
      <AnimatePresence>
        {showMatchModal && newMatch && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6"
            onClick={() => setShowMatchModal(false)}
          >
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-3xl p-8 max-w-sm w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-2xl font-serif text-sage-darker mb-2">It&apos;s a Match!</h2>
              <p className="text-muted-foreground mb-6">
                You and {newMatch.user?.name} liked each other
              </p>
              
              <div className="flex justify-center gap-4 mb-6">
                <Avatar className="w-20 h-20 border-4 border-sage/30">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-sage/20 text-sage-dark text-2xl">
                    {user?.name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="flex items-center">
                  <Heart className="w-8 h-8 text-red-400" fill="currentColor" />
                </div>
                <Avatar className="w-20 h-20 border-4 border-sage/30">
                  <AvatarImage src={newMatch.user?.picture} />
                  <AvatarFallback className="bg-sage/20 text-sage-dark text-2xl">
                    {newMatch.user?.name?.[0]}
                  </AvatarFallback>
                </Avatar>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setShowMatchModal(false)}
                  className="flex-1 border-sage/30 text-sage-darker hover:bg-sage/10 rounded-full"
                >
                  Keep Swiping
                </Button>
                <Button 
                  onClick={() => {
                    setShowMatchModal(false);
                    navigate(`/chat/${newMatch.connectionId}`);
                  }}
                  className="flex-1 bg-sage text-white hover:bg-sage-dark rounded-full"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Profile View Modal for Connection Requests */}
      <Dialog open={profileModalOpen} onOpenChange={(open) => { setProfileModalOpen(open); if (!open) setSelectedRequest(null); }}>
        <DialogContent className="bg-white border-sage/20 max-w-md max-h-[90vh] overflow-y-auto">
          {selectedRequest?.from_user && (
            <>
              <DialogHeader>
                <DialogTitle className="text-sage-darker font-serif text-center">
                  Connection Request
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Profile Header */}
                <div className="text-center">
                  <Avatar className="w-24 h-24 mx-auto border-4 border-sage/30">
                    <AvatarImage src={selectedRequest.from_user.picture} />
                    <AvatarFallback className="bg-sage/20 text-sage-dark text-3xl">
                      {selectedRequest.from_user.name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <h2 className="text-2xl font-serif text-sage-darker mt-4">
                    {selectedRequest.from_user.name}
                  </h2>
                  {selectedRequest.from_user.location && (
                    <p className="text-muted-foreground flex items-center gap-1 justify-center">
                      <MapPin className="w-4 h-4" />
                      {selectedRequest.from_user.location}
                    </p>
                  )}
                </div>

                {/* Profile Details */}
                <div className="space-y-4">
                  {/* Phase */}
                  {selectedRequest.from_user.phase && (
                    <div className="flex items-center gap-3 p-3 bg-sage/5 rounded-xl">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        selectedRequest.from_user.phase === 'ttc' ? 'bg-clay/20 text-clay-dark' :
                        selectedRequest.from_user.phase === 'pregnant' ? 'bg-sage/20 text-sage-dark' : 'bg-purple-100 text-purple-600'
                      }`}>
                        {getPhaseIcon(selectedRequest.from_user.phase)}
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Current Phase</p>
                        <p className="text-sage-darker font-medium">{getPhaseLabel(selectedRequest.from_user.phase)}</p>
                      </div>
                    </div>
                  )}

                  {/* Location */}
                  {selectedRequest.from_user.location && (
                    <div className="flex items-center gap-3 p-3 bg-sage/5 rounded-xl">
                      <div className="w-10 h-10 rounded-full bg-sage/20 flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-sage-dark" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Location</p>
                        <p className="text-sage-darker font-medium">{selectedRequest.from_user.location}</p>
                      </div>
                    </div>
                  )}

                  {/* Bio */}
                  {selectedRequest.from_user.bio && (
                    <div className="p-4 bg-sage/5 rounded-xl">
                      <p className="text-xs text-muted-foreground mb-2">About</p>
                      <p className="text-sage-darker">{selectedRequest.from_user.bio}</p>
                    </div>
                  )}

                  {/* Interests */}
                  {selectedRequest.from_user.interests?.length > 0 && (
                    <div className="p-4 bg-sage/5 rounded-xl">
                      <p className="text-xs text-muted-foreground mb-2">Interests</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedRequest.from_user.interests.map((interest, idx) => (
                          <Badge key={idx} variant="outline" className="border-sage/30 text-sage-dark">
                            {interest}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons - Show different buttons based on whether it's a pending request or connection */}
                {pendingRequests.some(r => r.request_id === selectedRequest.request_id) ? (
                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => handleDeclineRequest(selectedRequest.request_id)}
                      className="flex-1 border-sage/30 text-sage-darker hover:bg-sage/10 rounded-full"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Decline
                    </Button>
                    <Button
                      onClick={() => handleAcceptRequest(selectedRequest.request_id)}
                      className="flex-1 bg-sage text-white hover:bg-sage-dark rounded-full"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Accept
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setProfileModalOpen(false)}
                      className="flex-1 border-sage/30 text-sage-darker hover:bg-sage/10 rounded-full"
                    >
                      Close
                    </Button>
                    <Button
                      onClick={() => {
                        setProfileModalOpen(false);
                        navigate(`/chat/${selectedRequest.request_id}`);
                      }}
                      className="flex-1 bg-sage text-white hover:bg-sage-dark rounded-full"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Chat
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Discover Profile Preview Modal - Clean Design */}
      <Dialog open={discoverProfileModal} onOpenChange={(open) => {
        setDiscoverProfileModal(open);
        if (!open) setCurrentPhotoIndex(0);
      }}>
        <DialogContent className="bg-white border-sage/20 max-w-sm p-0 overflow-hidden">
          {selectedDiscoverUser && (
            <div className="flex flex-col">
              {/* Photo Gallery Section */}
              <div className="relative h-80">
                <img 
                  src={
                    selectedDiscoverUser.photos?.length > 0 
                      ? (selectedDiscoverUser.photos[currentPhotoIndex]?.startsWith('/api') 
                          ? `${API.replace('/api', '')}${selectedDiscoverUser.photos[currentPhotoIndex]}`
                          : selectedDiscoverUser.photos[currentPhotoIndex])
                      : selectedDiscoverUser.picture || "https://images.unsplash.com/photo-1607639106901-28314a0a622e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                  }
                  alt={selectedDiscoverUser.name}
                  className="w-full h-full object-cover"
                />
                
                {/* Photo Navigation - Left/Right arrows */}
                {selectedDiscoverUser.photos?.length > 1 && (
                  <>
                    <button 
                      onClick={() => setCurrentPhotoIndex(prev => prev > 0 ? prev - 1 : selectedDiscoverUser.photos.length - 1)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/40 hover:bg-black/60 rounded-full flex items-center justify-center text-white transition-colors"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button 
                      onClick={() => setCurrentPhotoIndex(prev => prev < selectedDiscoverUser.photos.length - 1 ? prev + 1 : 0)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/40 hover:bg-black/60 rounded-full flex items-center justify-center text-white transition-colors"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}
                
                {/* Photo dots indicator */}
                {selectedDiscoverUser.photos?.length > 1 && (
                  <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {selectedDiscoverUser.photos.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentPhotoIndex(idx)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          idx === currentPhotoIndex ? 'bg-white' : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                )}
                
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
                
                {/* Name & Phase overlay */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-2 mb-2">
                    <h2 className="text-2xl font-serif text-white">{selectedDiscoverUser.name}</h2>
                    {/* Phase Badge - Prominent */}
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      selectedDiscoverUser.phase === 'ttc' 
                        ? 'bg-amber-500 text-white' 
                        : selectedDiscoverUser.phase === 'pregnant' 
                          ? 'bg-emerald-500 text-white' 
                          : 'bg-purple-500 text-white'
                    }`}>
                      {getPhaseLabel(selectedDiscoverUser.phase)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-white/90 text-sm">
                    {selectedDiscoverUser.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {selectedDiscoverUser.location}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Content Section - Compact */}
              <div className="p-5 space-y-4">
                {/* Quick Info Tags */}
                <div className="flex flex-wrap gap-2">
                  {selectedDiscoverUser.children_info && (
                    <Badge variant="outline" className="border-sage/40 text-sage-darker bg-sage/5">
                      <Baby className="w-3.5 h-3.5 mr-1" />
                      {getChildrenLabel(selectedDiscoverUser.children_info)}
                    </Badge>
                  )}
                  {selectedDiscoverUser.working_status && (
                    <Badge variant="outline" className="border-sage/40 text-sage-darker bg-sage/5">
                      <Briefcase className="w-3.5 h-3.5 mr-1" />
                      {getWorkingLabel(selectedDiscoverUser.working_status)}
                    </Badge>
                  )}
                </div>

                {/* Bio */}
                {selectedDiscoverUser.bio && (
                  <p className="text-sage-darker text-sm leading-relaxed">
                    {selectedDiscoverUser.bio}
                  </p>
                )}

                {/* Interests */}
                {selectedDiscoverUser.interests?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedDiscoverUser.interests.slice(0, 5).map((interest, idx) => (
                      <span key={idx} className="text-xs px-2.5 py-1 bg-sage/10 text-sage-dark rounded-full">
                        {interest}
                      </span>
                    ))}
                    {selectedDiscoverUser.interests.length > 5 && (
                      <span className="text-xs px-2.5 py-1 bg-sage/10 text-sage-dark rounded-full">
                        +{selectedDiscoverUser.interests.length - 5}
                      </span>
                    )}
                  </div>
                )}

                {/* Help Topics */}
                {(selectedDiscoverUser.looking_for_help?.length > 0 || selectedDiscoverUser.willing_to_help?.length > 0) && (
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {selectedDiscoverUser.looking_for_help?.length > 0 && (
                      <div className="p-2.5 bg-rose-50 rounded-lg">
                        <p className="text-rose-600 font-semibold mb-1">Needs help</p>
                        <p className="text-rose-700/80 line-clamp-2">
                          {selectedDiscoverUser.looking_for_help.join(", ")}
                        </p>
                      </div>
                    )}
                    {selectedDiscoverUser.willing_to_help?.length > 0 && (
                      <div className="p-2.5 bg-green-50 rounded-lg">
                        <p className="text-green-600 font-semibold mb-1">Can help</p>
                        <p className="text-green-700/80 line-clamp-2">
                          {selectedDiscoverUser.willing_to_help.join(", ")}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDiscoverProfileModal(false);
                      setCurrentPhotoIndex(0);
                      handleSkip();
                    }}
                    className="flex-1 border-red-200 text-red-500 hover:bg-red-50 rounded-full h-11"
                  >
                    <X className="w-5 h-5 mr-2" />
                    Pass
                  </Button>
                  <Button
                    onClick={() => {
                      setDiscoverProfileModal(false);
                      setCurrentPhotoIndex(0);
                      handleConnect(selectedDiscoverUser.user_id);
                    }}
                    className="flex-1 bg-sage text-white hover:bg-sage-dark rounded-full h-11"
                  >
                    <Heart className="w-5 h-5 mr-2" />
                    Connect
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Navigation />
    </div>
  );
};

// Swipe Card Component with framer-motion
const SwipeCard = ({ user, onConnect, onSkip, onViewProfile, getPhaseIcon, getPhaseLabel }) => {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0.5, 1, 1, 1, 0.5]);

  const handleDragEnd = (event, info) => {
    if (info.offset.x > 100) {
      onConnect();
    } else if (info.offset.x < -100) {
      onSkip();
    }
  };

  const getChildrenLabel = (info) => {
    const labels = {
      'first-time-expecting': 'First-Time Expecting',
      'first-time-mom': 'First-Time Mom',
      'one-child': 'Mom of One',
      'two-children': 'Mom of Two',
      'three-plus': 'Mom of 3+',
      'twins': 'Twin Mom',
      'triplets-plus': 'Triplets or More'
    };
    return labels[info] || info;
  };

  const getWorkingLabel = (status) => {
    const labels = {
      'working-full-time': 'Working Full-Time',
      'working-part-time': 'Part-Time',
      'stay-at-home': 'Stay-at-Home Mom',
      'maternity-leave': 'Maternity Leave',
      'freelance': 'Freelance',
      'looking-for-work': 'Looking for Work',
      'student': 'Student'
    };
    return labels[status] || status;
  };

  return (
    <div className="relative">
      {/* Swipe indicators */}
      <motion.div 
        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-red-100 rounded-full p-4"
        style={{ opacity: useTransform(x, [-100, 0], [1, 0]) }}
      >
        <X className="w-8 h-8 text-red-500" />
      </motion.div>
      <motion.div 
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-sage/20 rounded-full p-4"
        style={{ opacity: useTransform(x, [0, 100], [0, 1]) }}
      >
        <Heart className="w-8 h-8 text-sage" />
      </motion.div>

      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        onDragEnd={handleDragEnd}
        style={{ x, rotate, opacity }}
        className="relative aspect-[3/4] max-h-[500px] rounded-3xl overflow-hidden cursor-grab active:cursor-grabbing shadow-xl"
        data-testid="swipe-card"
      >
        {/* Profile Image - Clickable area */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={(e) => { e.stopPropagation(); onViewProfile && onViewProfile(); }}
        >
          <img 
            src={user.photos?.[0] || user.picture || "https://images.unsplash.com/photo-1607639106901-28314a0a622e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"}
            alt={user.name}
            className="w-full h-full object-cover"
            draggable={false}
          />
        </div>
        
        {/* Additional photos indicator */}
        {user.photos && user.photos.length > 1 && (
          <div className="absolute top-4 right-4 flex gap-1 pointer-events-none">
            {user.photos.map((_, idx) => (
              <div key={idx} className={`w-2 h-2 rounded-full ${idx === 0 ? 'bg-white' : 'bg-white/50'}`} />
            ))}
          </div>
        )}
        
        {/* Tap to view hint */}
        <div className="absolute top-4 left-4 bg-black/40 backdrop-blur-sm rounded-full px-3 py-1.5 pointer-events-none">
          <span className="text-white/90 text-xs font-medium">Tap to view full profile</span>
        </div>
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />
        
        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6 space-y-2 pointer-events-none">
          <h2 className="text-2xl font-serif text-white">{user.name}</h2>
          
          {/* Location & Phase row */}
          <div className="flex flex-wrap items-center gap-2 text-white/90 text-sm">
            {user.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {user.location}
              </span>
            )}
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
              user.phase === 'ttc' 
                ? 'bg-amber-500 text-white' 
                : user.phase === 'pregnant' 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-purple-500 text-white'
            }`}>
              {getPhaseLabel(user.phase)}
            </span>
          </div>
          
          {user.bio && (
            <p className="text-white/90 text-sm line-clamp-2">{user.bio}</p>
          )}

          {/* Interests preview */}
          {user.interests && user.interests.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {user.interests.slice(0, 3).map((interest) => (
                <Badge key={interest} variant="outline" className="border-white/40 text-white/90 text-xs">
                  {interest}
                </Badge>
              ))}
              {user.interests.length > 3 && (
                <Badge variant="outline" className="border-white/40 text-white/70 text-xs">
                  +{user.interests.length - 3}
                </Badge>
              )}
            </div>
          )}
        </div>
      </motion.div>

      {/* Action buttons */}
      <div className="flex justify-center gap-6 mt-6">
        <Button 
          variant="outline" 
          size="lg"
          onClick={onSkip}
          className="w-16 h-16 rounded-full border-red-300 hover:bg-red-50 text-red-500"
          data-testid="skip-btn"
        >
          <X className="w-6 h-6" />
        </Button>
        <Button 
          size="lg"
          onClick={onConnect}
          className="w-16 h-16 rounded-full bg-sage hover:bg-sage-dark text-white"
          data-testid="connect-btn"
        >
          <Heart className="w-6 h-6" />
        </Button>
      </div>
    </div>
  );
};

export default Discover;
